import { useCallback, useState } from "react";

/**
 * Persists a small piece of UI state (e.g. a list-page filter selection) to
 * localStorage: the value is read once on init and written back on every
 * change. Returns a `[value, setValue]` pair just like `useState`.
 *
 * Pass an optional `parse` to coerce/validate the stored string into a valid
 * value (e.g. narrowing to a union); it is only called when a stored value is
 * present. When nothing is stored, `defaultValue` is used.
 */
export function useLocalStorageState<T extends string = string>(
  key: string,
  defaultValue: T,
  parse?: (stored: string) => T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    const stored = window.localStorage.getItem(key);
    if (stored === null) return defaultValue;
    return parse ? parse(stored) : (stored as T);
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(key, next);
      } catch {
        // Ignore persistence errors (e.g. storage disabled / quota exceeded).
      }
    },
    [key],
  );

  return [value, set];
}
